import { z } from 'zod';
import {
  MAX_PHOTO_FULL_BYTES,
  MAX_PHOTO_THUMB_BYTES,
  MAX_POST_PHOTOS,
  PHOTO_CAPTION_MAX,
  PHOTO_FULL_MAX_PX,
  PHOTO_MIMES,
  POST_STATUSES,
  POST_TYPES,
} from '../constants';
import { hmToMinutes } from '../time';
import { col, idSchema, minutesSchema, updatedAtSchema, type SheetRowResult } from './common';

export const latLngSchema = z.object({
  lat: z.number().min(-90).max(90),
  lng: z.number().min(-180).max(180),
});

const postFields = {
  code: z.string().trim().min(1).max(20),
  sectorId: idSchema,
  micro: z.string().trim().min(1).max(300),
  type: z.enum(POST_TYPES),
  urgent: z.boolean(),
  location: latLngSchema.nullable(),
  /** Rich text (sanitised HTML) shown on the briefing page. */
  instructions: z.string().max(20000),
  /** English instructions as the coordinator saved them; see `instructionsEnAuto`. */
  instructionsEn: z.string().max(20000),
  /** Replaces the card title the briefing derives from `micro` (`briefingPostHeading`). */
  briefingTitle: z.string().trim().max(200),
  gear: z.array(z.string().trim().min(1).max(100)),
  leadName: z.string().trim().max(200),
  leadPhone: z.string().trim().max(40),
  status: z.enum(POST_STATUSES),
};

export const postInputSchema = z.object({
  ...postFields,
  urgent: postFields.urgent.default(false),
  location: postFields.location.default(null),
  instructions: postFields.instructions.default(''),
  instructionsEn: postFields.instructionsEn.default(''),
  briefingTitle: postFields.briefingTitle.default(''),
  gear: postFields.gear.default([]),
  leadName: postFields.leadName.default(''),
  leadPhone: postFields.leadPhone.default(''),
  status: postFields.status.default('draft'),
});

/** Create also takes the post's labels, so a duplicate arrives labelled in one write. */
export const postCreateSchema = postInputSchema.extend({
  tagIds: z.array(idSchema).max(50).default([]),
});

/**
 * Built from the defaults-free field shapes, like `externalGroupUpdateSchema`:
 * `.partial()` keeps each key's `.default()`, so a PATCH without `status` would
 * parse to `status: 'draft'` and every Shrani on an active post would turn it
 * back into an OSNUTEK — and a PATCH of one field would blank the rest.
 */
export const postUpdateSchema = z
  .object(postFields)
  .partial()
  .extend({
    updatedAt: updatedAtSchema,
    /**
     * Sent with `instructionsEn` when the English is the translation exactly as
     * "Prevedi" returned it. Without it the English counts as written by hand,
     * and a later change to the Slovenian no longer replaces it.
     */
    instructionsEnAuto: z.boolean().optional(),
  })
  // `updatedAt` alone would reach the database as an empty `set` and throw.
  .refine((v) => Object.keys(v).length > 1, { message: 'ni sprememb' });

/**
 * Add and remove labels on several posts at once. A delta rather than the full
 * set, so two quick toggles on one post both land and a bulk change is one
 * transaction.
 */
export const postTagsDeltaSchema = z
  .object({
    postIds: z.array(idSchema).min(1).max(200),
    add: z.array(idSchema).max(50).default([]),
    remove: z.array(idSchema).max(50).default([]),
  })
  .refine((b) => b.add.length > 0 || b.remove.length > 0, {
    message: 'Navedi oznako, ki jo dodaš ali odstraniš.',
  })
  .refine((b) => !b.add.some((id) => b.remove.includes(id)), {
    message: 'Iste oznake ni mogoče hkrati dodati in odstraniti.',
  });
/** What a client sends; `add` and `remove` may be left out. */
export type PostTagsDelta = z.input<typeof postTagsDeltaSchema>;

export const postSchema = postInputSchema.extend({
  id: idSchema,
  createdAt: z.iso.datetime({ offset: true }),
  updatedAt: updatedAtSchema,
});

export const postWindowInputSchema = z
  .object({
    eventDayId: idSchema,
    fromMin: minutesSchema,
    toMin: minutesSchema,
  })
  .refine((r) => r.fromMin < r.toMin, { message: 'fromMin must be before toMin' });

export type PostInput = z.infer<typeof postInputSchema>;
export type Post = z.infer<typeof postSchema>;

export const sectorInputSchema = z.object({
  name: z.string().trim().min(1).max(200),
  location: latLngSchema.nullable().default(null),
});

export const sectorSchema = sectorInputSchema.extend({
  id: idSchema,
  updatedAt: updatedAtSchema,
});

// ---------------------------------------------------------------------------
// Post photos
//
// Two derivatives travel as base64 in one JSON body; the API decodes them,
// re-checks the byte lengths against the caps and sniffs the magic bytes. The
// string bounds here only keep an oversized body from reaching that decode.
// ---------------------------------------------------------------------------

const base64Blob = (maxBytes: number) =>
  z
    .string()
    .min(1)
    .max(Math.ceil(maxBytes / 3) * 4 + 8)
    .regex(/^[A-Za-z0-9+/]+={0,2}$/, 'blob mora biti base64');

export const postPhotoUploadSchema = z.object({
  /** Lightbox derivative, longest side <= PHOTO_FULL_MAX_PX. */
  full: base64Blob(MAX_PHOTO_FULL_BYTES),
  /** Filmstrip derivative, longest side <= PHOTO_THUMB_MAX_PX. */
  thumb: base64Blob(MAX_PHOTO_THUMB_BYTES),
  mime: z.enum(PHOTO_MIMES),
  /** Pixel size of `full`, used to reserve layout space before it loads. */
  width: z.number().int().min(1).max(PHOTO_FULL_MAX_PX),
  height: z.number().int().min(1).max(PHOTO_FULL_MAX_PX),
  captionSl: z.string().trim().max(PHOTO_CAPTION_MAX).default(''),
  captionEn: z.string().trim().max(PHOTO_CAPTION_MAX).default(''),
});
export type PostPhotoUpload = z.infer<typeof postPhotoUploadSchema>;

/**
 * Only the captions are mutable; bytes are immutable so their URL can be
 * cached forever. At least one field has to be present — an empty body would
 * reach the driver as an update with no columns, which is a 500, not a no-op.
 */
export const postPhotoPatchSchema = z
  .object({
    captionSl: z.string().trim().max(PHOTO_CAPTION_MAX).optional(),
    captionEn: z.string().trim().max(PHOTO_CAPTION_MAX).optional(),
  })
  .refine((b) => b.captionSl !== undefined || b.captionEn !== undefined, {
    message: 'Navedi vsaj en opis.',
  });

/** Full reorder: every photo of the post, in the order they should appear. */
export const postPhotoOrderSchema = z.object({
  ids: z.array(idSchema).min(1).max(MAX_POST_PHOTOS),
});

export const postPhotoSchema = z.object({
  id: idSchema,
  postId: idSchema,
  key: z.string(),
  sortIndex: z.number().int(),
  captionSl: z.string(),
  captionEn: z.string(),
  mime: z.enum(PHOTO_MIMES),
  width: z.number().int(),
  height: z.number().int(),
  byteSize: z.number().int(),
  createdAt: z.iso.datetime({ offset: true }),
  updatedAt: updatedAtSchema,
});
export type PostPhoto = z.infer<typeof postPhotoSchema>;

/**
 * What a volunteer gets: enough to render and caption the photo, and nothing
 * that identifies who uploaded it or when.
 */
export interface BriefingPhoto {
  key: string;
  captionSl: string;
  captionEn: string;
  width: number;
  height: number;
}

// ---------------------------------------------------------------------------
// CSV import — the coordinators' "Redarska mesta" Google Sheet, one tab per
// event day, exported as CSV. Sibling of `parseSheetVolunteerRow`: same lenient
// prefix header matching, same `{ ok, data?, error?, empty? }` result.
//
// The sheet's `IME IN PRIIMEK` column is deliberately not read. It holds the
// organising crew and partner teams (PGD, GRS, KK Završnica) pencilled onto
// posts — a different population from the volunteer roster, tracked outside
// this app. The race-distance flag columns and `v seznamu` are not read either;
// nothing links a post to a route, and `v seznamu` is a broken formula.
// ---------------------------------------------------------------------------

/** One shift row of the posts sheet, before any correction is applied. */
export interface SheetPostRow {
  /** `oznaka` — post code with its termin suffix, e.g. "KG5.3" or "Žir7". */
  code: string;
  /** `makro lokacija` — the sector name. */
  macro: string;
  /** `mikro lokacija` — where within the sector. */
  micro: string;
  /** `termin` — 1-based slot number within the day; 0 when the cell is blank. */
  termin: number;
  fromMin: number;
  toMin: number;
  /** `trajanje`, in minutes, as the sheet states it; null when blank. */
  statedMin: number | null;
  /** `opombe` — free text carried onto the shift. */
  note: string;
}

/**
 * Parse one row of the posts-sheet CSV.
 *
 * Times are taken verbatim, including the rows where `do` is at or before `od`
 * — those are shifts that run past midnight, and splitting them is the
 * importer's job, not the parser's.
 */
export function parseSheetPostRow(raw: Record<string, string>): SheetRowResult<SheetPostRow> {
  const code = col(raw, 'oznaka');
  const fromRaw = col(raw, 'od');
  const toRaw = col(raw, 'do');

  if (!code) {
    // Emptiness is judged on the columns a post is made of. The sheet's padding
    // rows are not blank — they carry the `trajanje` formula's 00:00, a broken
    // `v seznamu` reference and six FALSE race flags — and reading those as
    // content would report a hundred phantom errors per tab.
    const where = [col(raw, 'makro'), col(raw, 'mikro')].filter(Boolean).join(' / ');
    if (fromRaw || toRaw) return { ok: false, error: 'manjka oznaka' };
    if (where) {
      // The tabs end with a short roster of roving roles — "V avtu", "Na
      // kolesu", "V Bazi" — written as rows with a location and a person but
      // no code and no times. They are crew assignments, not posts.
      return { ok: false, skipped: `vrstica brez oznake in časa: ${where}` };
    }
    return { ok: false, empty: true };
  }

  const macro = col(raw, 'makro');
  if (!macro) return { ok: false, error: `»${code}«: manjka makro lokacija` };

  const micro = col(raw, 'mikro');
  if (!micro) return { ok: false, error: `»${code}«: manjka mikro lokacija` };

  const terminRaw = col(raw, 'termin');
  if (terminRaw !== '' && !/^\d{1,2}$/.test(terminRaw)) {
    return { ok: false, error: `»${code}«: neveljaven termin »${terminRaw}«` };
  }

  let fromMin: number;
  let toMin: number;
  try {
    fromMin = hmToMinutes(fromRaw);
    toMin = hmToMinutes(toRaw);
  } catch {
    return { ok: false, error: `»${code}«: neveljaven čas »${fromRaw}«–»${toRaw}«` };
  }

  const statedRaw = col(raw, 'trajanje');
  let statedMin: number | null = null;
  if (statedRaw !== '') {
    try {
      statedMin = hmToMinutes(statedRaw);
    } catch {
      return { ok: false, error: `»${code}«: neveljavno trajanje »${statedRaw}«` };
    }
  }

  return {
    ok: true,
    data: {
      code,
      macro,
      micro,
      termin: terminRaw === '' ? 0 : Number(terminRaw),
      fromMin,
      toMin,
      statedMin,
      note: col(raw, 'opombe'),
    },
  };
}
